import type { Component, DefineComponent } from "vue";
import { markRaw } from "vue";
import { SidePanelInterruptBehaviour, type SidePanelConfig, type UpdatablePanelConfig } from "../../shared/types/sidePanel";

interface SidePanelState {
	/** Currently displayed sidepanel (base layer) */
	current: SidePanelConfig | null;
	/** Additional panels rendered on top (for OVERLAY behavior) */
	stack: SidePanelConfig[];
	/** Queue of panels waiting to be shown */
	queue: SidePanelConfig[];
	/** Panels temporarily hidden by HIDE behavior */
	hidden: SidePanelConfig[];
	isOpen: boolean;
	history: string[];
}

export const useSidePanelManager = () => {
	const state = useState<SidePanelState>("sidepanelManager", () => ({
		current: null,
		stack: [],
		queue: [],
		hidden: [],
		isOpen: false,
		history: [],
	}));

	const currentSidePanel = computed(() => state.value.current);
	const isSidePanelOpen = computed(() => state.value.isOpen);
	const queueLength = computed(() => state.value.queue.length);
	const hasQueuedPanels = computed(() => state.value.queue.length > 0);
	const visiblePanels = computed(() => {
		const panels = [];
		if (state.value.current) panels.push(state.value.current);
		panels.push(...state.value.stack);
		return panels;
	});

	const sortQueue = () => {
		state.value.queue.sort((a, b) => b.priority - a.priority);
	};

	const getNextToShow = (): SidePanelConfig | null => {
		if (state.value.queue.length === 0) return null;

		// Prefer non-persistent items
		const nonPersistent = state.value.queue.filter(
			(item) => !item.persistent
		);
		const candidate =
			nonPersistent.length > 0 ? nonPersistent[0] : state.value.queue[0];

		return candidate || null;
	};

	/**
	 * Handle showing a panel based on its interrupt behavior
	 */
	const handleInterruptBehavior = (config: SidePanelConfig): boolean => {
		const behavior = config.SidePanelInterruptBehaviour || SidePanelInterruptBehaviour.WAIT;

		// If nothing is currently displayed, just show it
		if (!state.value.current) {
			return true; // Proceed with normal opening
		}

		switch (behavior) {
			case SidePanelInterruptBehaviour.WAIT:
				// Add to queue and wait for current to close - DO NOT interrupt
				return false;

			case SidePanelInterruptBehaviour.HIDE:
				// Hide current panel, show this one
				if (state.value.current) {
					const currentPanel = state.value.current;
					currentPanel.hiddenBy = config.id;
					state.value.hidden.push(currentPanel);
					state.value.current = null;
					currentPanel.onHide?.();
				}
				return true; // Proceed with opening

			case SidePanelInterruptBehaviour.OVERLAY:
				// Add to stack (both panels visible)
				state.value.stack.push(config);
				state.value.history.push(config.id);

				nextTick(() => {
					config.onOpen?.();
				});

				return false; // Already handled, don't proceed with normal opening

			case SidePanelInterruptBehaviour.REPLACE:
				// Replace current if higher priority
				if (state.value.current && config.priority > state.value.current.priority) {
					const currentPanel = state.value.current;
					if (currentPanel.persistent) {
						// Move current back to queue
						if (!state.value.queue.find(item => item.id === currentPanel.id)) {
							state.value.queue.push(currentPanel);
							sortQueue();
						}
					} else {
						// Close current panel
						currentPanel.onClose?.();
					}
					state.value.current = null;
					return true; // Proceed with opening
				}
				return false; // Don't interrupt if not higher priority

			default:
				return false;
		}
	};

	/**
	 * Restore hidden panels when the panel that hid them closes
	 */
	const restoreHiddenPanels = (closingPanelId: string) => {
		const toRestore = state.value.hidden.filter(
			panel => panel.hiddenBy === closingPanelId
		);

		if (toRestore.length > 0) {
			// Remove from hidden array
			state.value.hidden = state.value.hidden.filter(
				panel => panel.hiddenBy !== closingPanelId
			);

			// Restore the most recently hidden panel
			const panelToRestore = toRestore[toRestore.length - 1];
			if (panelToRestore) {
				state.value.current = panelToRestore;
				state.value.isOpen = true;

				nextTick(() => {
					panelToRestore.onRestore?.();
				});
			}

			// Put the rest back in queue
			toRestore.slice(0, -1).forEach(panel => {
				if (!state.value.queue.find(item => item.id === panel.id)) {
					state.value.queue.push(panel);
				}
			});
			sortQueue();
		}
	};

	const openSidePanel = (config: SidePanelConfig) => {
		// Check if already open or in queue
		if (state.value.current?.id === config.id) {
			console.warn(`SidePanel with id "${config.id}" is already open`);
			return null;
		}

		if (state.value.stack.some(panel => panel.id === config.id)) {
			console.warn(`SidePanel with id "${config.id}" is already in stack`);
			return null;
		}

		// Mark the component as raw to prevent reactivity
		const processedConfig = {
			...config,
			component: typeof config.component === 'string'
				? config.component
				: markRaw(config.component)
		};

		// Update if already in queue, otherwise add to queue
		const existingIndex = state.value.queue.findIndex(
			(item) => item.id === config.id
		);
		if (existingIndex !== -1) {
			state.value.queue[existingIndex] = processedConfig;
		} else {
			state.value.queue.push(processedConfig);
		}

		sortQueue();

		const nextToShow = getNextToShow();
		if (!nextToShow) return null;

		// Handle interrupt behavior
		const shouldOpen = handleInterruptBehavior(nextToShow);

		if (shouldOpen) {
			// Remove from queue
			const indexToRemove = state.value.queue.findIndex(
				(item) => item.id === nextToShow.id
			);
			if (indexToRemove !== -1) {
				state.value.queue.splice(indexToRemove, 1);
			}

			// Set as current and open
			state.value.current = nextToShow;
			state.value.isOpen = false;
			state.value.history.push(nextToShow.id);

			// Trigger opening animation
			nextTick(() => {
				state.value.isOpen = true;

				nextTick(() => {
					nextToShow.onOpen?.();
				});
			});
		}

		return processedConfig.id;
	};

	const closeCurrentSidePanel = () => {
		if (!state.value.current) return;

		const closingPanel = state.value.current;

		// Call onClose immediately (before animation)
		closingPanel.onClose?.();

		state.value.isOpen = false;

		setTimeout(() => {
			const closingPanelId = closingPanel.id;
			state.value.current = null;

			// Call onClosed after animation completes
			closingPanel.onClosed?.();

			nextTick(() => {
				// Check if any panels were hidden by this one
				restoreHiddenPanels(closingPanelId);

				// Only show next if nothing was restored
				if (!state.value.current) {
					showNextSidePanel();
				}
			});
		}, 350);
	};

	const closeSidePanel = (id: string) => {
		// Check if it's in the stack
		const stackIndex = state.value.stack.findIndex((item) => item.id === id);
		if (stackIndex !== -1) {
			const removed = state.value.stack.splice(stackIndex, 1)[0];
			removed?.onClose?.();
			removed?.onClosed?.();  // Stack items close instantly

			// Restore any panels hidden by this one
			restoreHiddenPanels(id);
			return true;
		}

		// Check if it's the current panel
		if (state.value.current?.id === id) {
			closeCurrentSidePanel();
			return true;
		}

		// Check if it's in the queue
		const queueIndex = state.value.queue.findIndex((item) => item.id === id);
		if (queueIndex !== -1) {
			const removed = state.value.queue.splice(queueIndex, 1)[0];
			removed?.onClose?.();
			removed?.onClosed?.();  // Queue items were never shown
			return true;
		}

		// Check if it's hidden
		const hiddenIndex = state.value.hidden.findIndex((item) => item.id === id);
		if (hiddenIndex !== -1) {
			const removed = state.value.hidden.splice(hiddenIndex, 1)[0];
			removed?.onClose?.();
			removed?.onClosed?.();  // Hidden items close instantly
			return true;
		}

		return false;
	};

	const showNextSidePanel = () => {
		const nextToShow = getNextToShow();

		if (nextToShow) {
			const indexToRemove = state.value.queue.findIndex(
				(item) => item.id === nextToShow.id
			);
			if (indexToRemove !== -1) {
				state.value.queue.splice(indexToRemove, 1);
			}

			state.value.current = nextToShow;
			state.value.isOpen = true;
			state.value.history.push(nextToShow.id);

			nextTick(() => {
				nextToShow.onOpen?.();
			});
		}
	};

	const closeAllSidePanels = () => {
		// Close stack
		state.value.stack.forEach((item) => {
			item.onClose?.();
			item.onClosed?.();
		});

		// Close current
		if (state.value.current) {
			state.value.current.onClose?.();
			state.value.current.onClosed?.();
		}

		// Close queue
		state.value.queue.forEach((item) => {
			item.onClose?.();
			item.onClosed?.();
		});

		// Close hidden
		state.value.hidden.forEach((item) => {
			item.onClose?.();
			item.onClosed?.();
		});

		state.value.current = null;
		state.value.stack = [];
		state.value.queue = [];
		state.value.hidden = [];
		state.value.isOpen = false;
	};

	const isSidePanelQueued = (id: string): boolean => {
		return (
			state.value.current?.id === id ||
			state.value.stack.some((item) => item.id === id) ||
			state.value.queue.some((item) => item.id === id) ||
			state.value.hidden.some((item) => item.id === id)
		);
	};

	const updateCurrentProps = (newProps: Record<string, any>) => {
		if (state.value.current) {
			state.value.current.props = {
				...state.value.current.props,
				...newProps,
			};
		}
	};

	/**
	 * Update configuration of the currently displayed panel.
	 * @param updates - Partial config object with properties to update
	 * @returns true if update was applied, false if no current panel
	 */
	const updateCurrentPanel = (updates: UpdatablePanelConfig): boolean => {
		if (!state.value.current) {
			console.warn('updateCurrentPanel: No panel is currently displayed');
			return false;
		}

		if (updates.props !== undefined) {
			state.value.current.props = {
				...state.value.current.props,
				...updates.props,
			};
		}

		const { props, ...otherUpdates } = updates;
		Object.assign(state.value.current, otherUpdates);

		return true;
	};

	/**
	 * Update configuration of a specific panel by ID.
	 * Searches through current, stack, queue, and hidden arrays.
	 * @param id - The panel ID to update
	 * @param updates - Partial config object with properties to update
	 * @returns true if panel was found and updated, false otherwise
	 */
	const updatePanel = (id: string, updates: UpdatablePanelConfig): boolean => {
		const applyUpdates = (panel: SidePanelConfig): void => {
			if (updates.props !== undefined) {
				panel.props = {
					...panel.props,
					...updates.props,
				};
			}
			const { props, ...otherUpdates } = updates;
			Object.assign(panel, otherUpdates);
		};

		// Check current panel
		if (state.value.current?.id === id) {
			applyUpdates(state.value.current);
			return true;
		}

		// Check stack
		const stackPanel = state.value.stack.find(panel => panel.id === id);
		if (stackPanel) {
			applyUpdates(stackPanel);
			return true;
		}

		// Check queue
		const queuePanel = state.value.queue.find(panel => panel.id === id);
		if (queuePanel) {
			applyUpdates(queuePanel);
			return true;
		}

		// Check hidden
		const hiddenPanel = state.value.hidden.find(panel => panel.id === id);
		if (hiddenPanel) {
			applyUpdates(hiddenPanel);
			return true;
		}

		console.warn(`updatePanel: Panel with id "${id}" not found`);
		return false;
	};

	const getQueueInfo = () => {
		return {
			current: state.value.current?.id || null,
			stack: state.value.stack.map((item) => ({
				id: item.id,
				priority: item.priority,
				zIndex: item.zIndex,
			})),
			queue: state.value.queue.map((item) => ({
				id: item.id,
				priority: item.priority,
				persistent: item.persistent || false,
				SidePanelInterruptBehaviour: item.SidePanelInterruptBehaviour || SidePanelInterruptBehaviour.WAIT,
			})),
			hidden: state.value.hidden.map((item) => ({
				id: item.id,
				hiddenBy: item.hiddenBy,
			})),
			history: [...state.value.history],
		};
	};

	const clearHistory = () => {
		state.value.history = [];
	};

	const PRIORITY = {
		LOW: 1,
		NORMAL: 5,
		HIGH: 10,
		CRITICAL: 20,
		SYSTEM: 100,
	} as const;

	const createSidePanel = (
		component: Component | DefineComponent,
		props: Record<string, any> = {},
		options: Partial<Omit<SidePanelConfig, "component" | "props">> = {}
	) => {
		// Check if that component is already visible
		const isVisible =
			state.value.current?.component === component ||
			state.value.stack.some(panel => panel.component === component);

		if (isVisible) {
			// Just ensure it's open
			state.value.isOpen = true;
			return null;
		}

		const id = options.id || `sidepanel-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

		return openSidePanel({
			id,
			component,
			props: { ...props },
			priority: PRIORITY.NORMAL,
			SidePanelInterruptBehaviour: SidePanelInterruptBehaviour.WAIT, // Default: don't interrupt
			size: 'medium',
			showCloseButton: true,
			closeOnBackdropClick: true,
			preventScroll: true,
			...options,
		});
	};

	return {
		// Computed state
		currentSidePanel: readonly(currentSidePanel),
		isSidePanelOpen: readonly(isSidePanelOpen),
		queueLength: readonly(queueLength),
		hasQueuedPanels: readonly(hasQueuedPanels),
		visiblePanels: readonly(visiblePanels),

		// Actions
		openSidePanel,
		closeCurrentSidePanel,
		closeSidePanel,
		closeAllSidePanels,
		showNextSidePanel,
		updateCurrentProps,
		updateCurrentPanel,
		updatePanel,

		// Helper
		createSidePanel,

		// Query
		isSidePanelQueued,
		getQueueInfo,
		clearHistory,

		// Constants
		PRIORITY,
		SidePanelInterruptBehaviour,
	};
};
